import { useSyncExternalStore } from 'react';
import { currentClient } from '@lib/clients';

/**
 * OPEN CASES IN: the side panel or a pop out. William, 2 Oct 2026 (verbatim): "each time she clicks on
 * a user - put a switch at the top of the portal - pop out or side panel. So, she can choose to have a
 * pop out for each selection or a side view. Either way, each should be the same and follow this
 * simplification."
 *
 * THREE CHOICES as the default presentation. William, 2 Oct 2026 (verbatim): "yes - give her three
 * choices as a default presentation." In the switch's order: Under the row (his 24 Sep picture 7, the
 * member view under the row), Side panel, Pop out.
 *
 * ONE choice for the whole console, set from the switch in the top bar (AppShell) and remembered per
 * browser under OPEN_MODE_KEY. The side panel is the default. Every page that opens a case from a row
 * calls openEvent(id, select):
 *   - under the row: select(id). The Inbound queue's GROUPED view opens the member view (MemberView)
 *     under the row holding that case; every other list has no row to open under (the flat view,
 *     Calling, Consent chase, Archive entries, Live call, the Opt In lanes), so there it is exactly
 *     the side panel;
 *   - side panel: select(id), and the page renders its EventDrawer exactly as before;
 *   - pop out: the case opens in its own window, the same /event/<id> page the drawer's own "Pop out"
 *     button opens, and nothing is selected, so no side panel opens on that page.
 *
 * Storage is a convenience, never a requirement: every read and write is guarded, a blocked store
 * starts on the side panel, and the choice made in this tab holds for the session either way. A second
 * window of the console follows a change through the browser's storage event.
 */

export type OpenMode = 'below' | 'side' | 'popout';

/** The switch's chips, in his order (the top bar renders them in this order; the tests read it). */
export const OPEN_MODES: readonly OpenMode[] = ['below', 'side', 'popout'];

export const OPEN_MODE_KEY = 'membercare.openMode';
export const DEFAULT_OPEN_MODE: OpenMode = 'side';

/** The switch's words, one place (the top bar renders them; the tests read them). */
export const OPEN_MODE_WORDING = {
  label: 'Open cases in',
  below: 'Under the row',
  side: 'Side panel',
  popout: 'Pop out',
  belowHelp: "Open each case under its row, with the member's history",
  sideHelp: 'Open each case in the panel at the side of this page',
  popoutHelp: 'Open each case in its own window',
} as const;

function isOpenMode(v: unknown): v is OpenMode {
  return v === 'below' || v === 'side' || v === 'popout';
}

function readStored(): OpenMode | null {
  try {
    const v = window.localStorage.getItem(OPEN_MODE_KEY);
    return isOpenMode(v) ? v : null;
  } catch {
    return null; // blocked storage: the default (or this session's choice) stands
  }
}

/** This tab's choice; null until first read, so the stored value is read lazily and once. */
let mode: OpenMode | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of [...listeners]) l();
}

export function getOpenMode(): OpenMode {
  if (mode === null) mode = readStored() ?? DEFAULT_OPEN_MODE;
  return mode;
}

export function setOpenMode(m: OpenMode): void {
  if (!isOpenMode(m)) return;
  mode = m;
  try {
    window.localStorage.setItem(OPEN_MODE_KEY, m);
  } catch {
    /* blocked storage: the choice holds in memory for this session only */
  }
  notify();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** A second window of the console follows the switch: the browser tells every other tab of the change. */
function onStorage(e: StorageEvent): void {
  if (e.key !== OPEN_MODE_KEY) return;
  const next = isOpenMode(e.newValue) ? e.newValue : DEFAULT_OPEN_MODE;
  if (next === mode) return;
  mode = next;
  notify();
}

if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);

/** React hook: the current choice, re-rendering on every change (no provider needed). */
export function useOpenMode(): OpenMode {
  return useSyncExternalStore(subscribe, getOpenMode, () => DEFAULT_OPEN_MODE);
}

/**
 * The case's own page, NAMING THE PLAN (16 Oct 2026). A pop out is a fresh window: before this it named
 * no plan, read the remembered choice against the two-plan floor, and asked for a BCBSM case under
 * Alignment, which answered 404 and left the window on "Loading event...". The page now carries the
 * plan as the `?client=` hint, and the pop out waits for the plan list before it reads the case.
 */
export function eventPageUrl(id: string): string {
  return `/event/${encodeURIComponent(id)}?client=${encodeURIComponent(currentClient())}`;
}

/** The windows this tab has popped out, by event id, so a second click brings a window forward. */
const popouts = new Map<string, Window>();

/**
 * The case in its own window: exactly the drawer's own "Pop out" call. Same origin and NO noopener: a
 * same-origin window.open COPIES sessionStorage into the child, so the MSAL cache rides along and the
 * pop out signs in silently (never pass noopener here, it suppresses that copy).
 *
 * A case this tab already popped out, whose window is still open, is brought forward instead of opened
 * again: calling window.open on the same name would navigate that window afresh and throw away what
 * the operator had typed there.
 */
export function popOutEvent(id: string): Window | null {
  const open = popouts.get(id);
  if (open && !open.closed) {
    try {
      open.focus();
      return open;
    } catch {
      /* the window cannot be reached any more; open it again below */
    }
  }
  const w = window.open(eventPageUrl(id), `mc-event-${id}`, 'popup=yes,width=1080,height=1000');
  if (w) popouts.set(id, w);
  else popouts.delete(id);
  return w ?? null;
}

/**
 * Open a case the way the switch says. Side panel: select(id) (the page shows its drawer). Under the
 * row: select(id) as well; the Inbound queue's grouped view shows the selection under its row, and
 * every other page, having no row to open under, shows its drawer exactly as for the side panel. Pop
 * out: the case in its own window, and nothing selected, so no drawer opens on the page.
 */
export function openEvent(id: string, select: (id: string) => void): void {
  if (getOpenMode() === 'popout') {
    popOutEvent(id);
    return;
  }
  select(id);
}

/** Tests only: forget this tab's choice and its pop-out windows, so the next read starts from storage. */
export function resetOpenModeForTests(): void {
  mode = null;
  popouts.clear();
}
